import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";

import { ISentryEnabledConfig, parseSentryConfig } from "./config";

// Served by nginx from the operator-managed `ph-spa-config` ConfigMap.
const SENTRY_CONFIG_URL = "/config/sentry.json";

// Query and fragment values can be secrets (the password-reset token `t`, the
// OIDC `code` and `state`), so they are replaced in breadcrumbs, performance
// entries and event and replay URLs. Snapshot addresses cannot be scrubbed; see
// startReplayOnCleanUrl. Parameter names stay, which keeps e.g. `errorCode=`
// readable. Backslashes end a value so a JSON-escaped quote survives the round
// trip. A name after `#` cannot hold brackets, which leaves CSS selectors such
// as `button#id[type="submit"]` in click breadcrumbs alone, while query names
// such as `ids[]` are still scrubbed.
export function scrubUrls(text: string): string {
  return text.replace(
    /(?<name>(?:[?&][^=&#\s"'\\]+|#[^=&#\s"'\\[\]]+)=)[^&#\s"'\\]*/g,
    "$<name>[Filtered]",
  );
}

type ReplayOptions = NonNullable<
  Parameters<typeof Sentry.replayIntegration>[0]
>;
type RecordingEventHook = NonNullable<ReplayOptions["beforeAddRecordingEvent"]>;

// The SDK passes only custom events (breadcrumbs and performance spans) here.
const scrubRecordingEvent: RecordingEventHook = (event) => {
  return JSON.parse(scrubUrls(JSON.stringify(event)));
};

// Everything is masked: text and inputs become same-length placeholders, so
// layout, row counts and clicks stay visible while emails, names and card data
// do not. The three flags are SDK defaults, pinned so an upgrade cannot relax
// them. Payment widgets are blocked outright (an empty box of the same size).
// Request and response bodies are not recorded (no networkDetailAllowUrls).
export const REPLAY_OPTIONS = {
  maskAllText: true,
  maskAllInputs: true,
  blockAllMedia: true,
  block: [
    "iframe",
    ".recurly-element",
    "#ams-3d-secure",
    "#pay-with-amazon",
    ".apple-pay-button",
    "#google-pay-button-container",
  ],
  beforeAddRecordingEvent: scrubRecordingEvent,
};

// Console arguments are dropped: they hold raw objects scrubUrls cannot reach.
// The message (strings joined, objects as [object Object]) is scrubbed below.
export function scrubBreadcrumb(
  breadcrumb: Sentry.Breadcrumb,
): Sentry.Breadcrumb {
  const data =
    breadcrumb.data &&
    Object.fromEntries(
      Object.entries(breadcrumb.data)
        .filter(([key]) => {
          return !(breadcrumb.category === "console" && key === "arguments");
        })
        .map(([key, value]) => {
          return [key, typeof value === "string" ? scrubUrls(value) : value];
        }),
    );

  return {
    ...breadcrumb,
    message: breadcrumb.message && scrubUrls(breadcrumb.message),
    data,
  };
}

// Covers error events and the replay event, whose `urls` lists the pages of the
// session.
export function scrubEvent(event: Sentry.Event): Sentry.Event {
  const { request } = event;

  if (request?.url) {
    request.url = scrubUrls(request.url);
  }
  if (request?.headers?.Referer) {
    request.headers.Referer = scrubUrls(request.headers.Referer);
  }
  // `urls` exists only on replay events, whose type @sentry/react does not export.
  if (event.type === "replay_event") {
    const replayEvent = event as Sentry.Event & { urls?: string[] };
    replayEvent.urls = replayEvent.urls?.map(scrubUrls);
  }

  return event;
}

// Every recording snapshot stores the page's full address, which the SDK gives
// no hook to scrub. Secrets reach the portal only in full page loads, whose
// pages drop them with history.replace (reset page, OIDC callback), so replay
// is added only on an address with no query string or fragment. A new route
// that receives a secret in its address must drop it the same way.
export function startReplayOnCleanUrl(): void {
  const client = Sentry.getClient();

  if (
    client &&
    !client.getIntegrationByName("Replay") &&
    window.location.search === "" &&
    window.location.hash === ""
  ) {
    // A failure here must not reach the error boundary and blank the portal.
    try {
      Sentry.addIntegration(Sentry.replayIntegration(REPLAY_OPTIONS));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Sentry replay could not start", error);
    }
  }
}

async function loadSentryConfig() {
  const response = await fetch(SENTRY_CONFIG_URL, { cache: "no-store" });

  // nginx answers a missing file with index.html and status 200, so the
  // content type is what tells a missing ConfigMap apart.
  const contentType = response.headers.get("content-type");
  if (!response.ok || !contentType?.includes("application/json")) {
    throw new Error(
      `${SENTRY_CONFIG_URL} is missing (HTTP ${response.status}, ${contentType}); is the ph-spa-config ConfigMap mounted?`,
    );
  }

  return parseSentryConfig(await response.json());
}

function startSentry(config: ISentryEnabledConfig) {
  Sentry.init({
    dsn: config.dsn,
    environment: config.environment,
    replaysSessionSampleRate: config.replaysSessionSampleRate,
    replaysOnErrorSampleRate: config.replaysOnErrorSampleRate,
    beforeBreadcrumb: scrubBreadcrumb,
  });
  Sentry.addEventProcessor(scrubEvent);
  startReplayOnCleanUrl();
}

// A missing or broken config turns monitoring off but never blocks the portal
// (see README "Runtime config"). The portal renders while the config loads, so
// errors thrown before then are not reported.
export async function initSentry(): Promise<void> {
  let config;

  try {
    config = await loadSentryConfig();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Sentry is off: its config could not be loaded", error);
    return;
  }

  if (!config.enabled) {
    // eslint-disable-next-line no-console
    console.info("Sentry is off: disabled in its config");
    return;
  }
  if (!config.hosts.includes(window.location.host)) {
    // eslint-disable-next-line no-console
    console.info(`Sentry is off: ${window.location.host} is not in its hosts`);
    return;
  }

  startSentry(config);
}

// Tags errors and replays with the signed-in principal id (no email or name),
// so support can find a customer's replay from the id shown in Dive.
export function useSentryUser(principalId: string | undefined): void {
  useEffect(() => {
    Sentry.setUser(principalId ? { id: principalId } : null);
  }, [principalId]);
}

// Re-checks the address on every in-app navigation (must render inside Router).
export function useReplayOnCleanUrl(): void {
  const location = useLocation();

  useEffect(startReplayOnCleanUrl, [location]);
}
